import { useState } from "react";

/* PACKAGES */
import { Link } from "react-router-dom";
import { Button } from "@material-tailwind/react";
import { motion } from "framer-motion";

/* ICONS */
import { FiArrowUpRight } from "react-icons/fi";
import { MdAdd } from "react-icons/md";
import { FiMinus } from "react-icons/fi";
import { IoClose } from "react-icons/io5";

/* CONTAINER ANIMATION */
const containerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 1 },
  },
};

/* DIV ANIMATION */
const animation = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
  hidden: {
    opacity: 0,
    y: 20,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
};

const MyBag = () => {
  const [quantity, setQuantity] = useState(1);

  /* QUANTITY ADD HANDLER */
  const addQuantity = () => {
    setQuantity(quantity + 1);
  };

  /* QUALITY SUBTRACT HANDLER */
  const subtractQuantity = () => {
    if (quantity === 1) {
      setQuantity(1);
    } else {
      setQuantity(quantity - 1);
    }
  };

  /* QUANTITY BOX */
  const QuantityBox = (props) => {
    return (
      <div className="flex items-center">
        <Button
          className="h-9 shadow-none hover:shadow-none bg-white border border-r-0 border-greyBorder !rounded-l-lg !rounded-r-none p-2 flex items-center justify-center"
          onClick={subtractQuantity}
        >
          <FiMinus className="w-4 h-4 text-black" />
        </Button>
        <div className="w-[5vh] h-9">
          <input
            type="number"
            name="quantity"
            id="quantity"
            className="w-full h-full block font-source font-semibold placeholder:font-source placeholder:font-normal text-sm text-black p-0 focus:outline-none border border-greyBorder text-center"
            value={props.value}
            onChange={props.onChange}
          />
        </div>
        <Button
          className="h-9 shadow-none hover:shadow-none bg-white border border-l-0 border-greyBorder !rounded-l-none !rounded-r-lg p-2 flex items-center justify-center"
          onClick={addQuantity}
        >
          <MdAdd className="w-4 h-4 text-black" />
        </Button>
      </div>
    );
  };

  /* PRODUCT TILE */
  const ProductTile = (props) => {
    return (
      <div className="w-full bg-greyBg bg-opacity-70 rounded-lg p-5 relative">
        <div className="flex items-center gap-5 xl:gap-10 lg:gap-10">
          <div className="w-[30%] xl:w-[15%] lg:w-[15%] flex-shrink-0 relative">
            <div className="bg-white border border-greyBorder rounded-lg flex items-center justify-center py-2">
              <img
                src={props.image}
                className="w-4/5 h-32 object-contain"
                alt="Product name"
              />
            </div>
          </div>
          <div className="w-[70%] xl:w-[75%] lg:w-[75%]">
            <h5 className="text-lg xl:text-xl lg:text-xl font-source font-bold text-black leading-tight">
              {props.name}{" "}
            </h5>
            <p className="text-lg xl:text-xl lg:text-xl font-source font-semibold text-primaryRed tracking-tight mt-2">
              ${props.price}
            </p>
            <div className="mt-2">
              <QuantityBox
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* REMOVE BUTTON */}
        <Button className="shadow-none hover:shadow-none p-2 hover:bg-black hover:bg-opacity-10 rounded-full bg-transparent transition-all duration-300 ease-in-out absolute top-1 xl:top-5 lg:top-5 right-1 xl:right-5 lg:right-5">
          <IoClose className="text-black w-4 h-4" />
        </Button>
      </div>
    );
  };

  /* ORDER SUMMARY TILE */
  const OrderSummaryTile = (props) => {
    return (
      <div className="flex items-center justify-between first:mt-0 mt-2">
        <h5 className="text-base font-source font-normal text-headerGrey">
          {props.label}
        </h5>
        <h5 className="text-lg font-source font-bold text-black tracking-tight">
          ${props.value}
        </h5>
      </div>
    );
  };

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-white py-6 xl:py-14 lg:py-14"
    >
      {/* BAG ITEMS */}
      <motion.div variants={animation} className="pb-6 xl:pb-14 lg:pb-14">
        <div className="container">
          {/* HEADING */}
          <motion.div
            variants={animation}
            className="block xl:flex lg:flex items-center justify-between"
          >
            <div>
              <h1 className="text-3xl xl:text-4xl lg:text-4xl font-fraunces-bold tracking-tight text-black">
                Bag items (5)
              </h1>
              <h5 className="text-lg font-source font-normal text-headerGrey">
                These are the items that you added to the bag. Complete your
                purchase now to secure them.
              </h5>
            </div>
          </motion.div>

          {/* ITEMS LIST */}
          <motion.div
            variants={animation}
            className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-y-5 xl:gap-20 lg:gap-20 mt-5 xl:mt-10 lg:mt-10"
          >
            {/* PRODUCT */}
            <div className="col-span-2">
              <ProductTile
                image="https://d1pd8tx4g8m009.cloudfront.net/product-images/Black-Beige-Stylish-Typography-Business-Tips-Pinterest-Pin-3-17553651615852025-08-16-17-26-01nROd1.png"
                name="Taiwan Ocean Bomb 315ml Sparkling Water"
                price="1,151.00"
              />
            </div>

            {/* ORDER SUMMARY */}
            <div className="col-span-1">
              <div className="border border-[#E5E5E5] rounded-lg p-5">
                <OrderSummaryTile label="Items (1)" value="1,151.00" />
                <div className="mt-3 border-t border-[#E5E5E5]">
                  <div className="flex items-center justify-between pt-3">
                    <div>
                      <h5 className="text-lg font-source font-semibold text-black">
                        Total
                      </h5>
                      <p className="text-sm font-source font-normal text-headerGrey">
                        (Exclusive of all taxes)
                      </p>
                    </div>
                    <h5 className="text-xl font-source font-bold text-black tracking-tight">
                      $1,151.00
                    </h5>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Link to="/checkout">
                  <Button className="w-full xl:w-fit lg:w-fit mt-5 shadow-none rounded-full hover:shadow-none bg-primaryRed text-base font-source font-semibold text-white hover:bg-opacity-80  normal-case transition-all duration-300 ease-in-out">
                    Proceed to checkout
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default MyBag;
